import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { 
  Home, 
  MapPin, 
  Calendar, 
  Users, 
  Trophy, 
  Gift, 
  ShoppingBag,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  Sun,
  Moon,
  Bell,
  User,
  CreditCard
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Book Court', href: '/book/venue_camsur', icon: MapPin },
    { name: 'My Bookings', href: '/my-bookings', icon: Calendar },
    // { name: 'Open Play', href: '/open-play', icon: Users },
    // { name: 'Players', href: '/players', icon: Users }, // Temporarily hidden
    { name: 'Events', href: '/events', icon: Trophy },
    { name: 'Add-ons', href: '/add-ons', icon: ShoppingBag },
    // { name: 'Rewards', href: '/rewards', icon: Gift },
    { name: 'Memberships', href: '/memberships', icon: CreditCard },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href.split('?')[0]);

  const NavLink = ({ item, mobile = false }) => (
    <Link
      to={item.href}
      className={`flex items-center ${mobile ? 'px-4 py-3' : 'px-3 py-2'} rounded-lg transition-colors ${
        isActive(item.href)
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
    >
      <item.icon className={`h-5 w-5 ${mobile ? 'mr-3' : 'mr-2'}`} />
      <span className={mobile ? 'font-medium' : 'text-sm font-medium'}>{item.name}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Mobile Menu */}
            <div className="flex items-center space-x-4">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0">
                  <div className="flex flex-col h-full">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center space-x-3">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_pbclub-scheduler/artifacts/s9rwat8e_pickleballlogo.jpg" 
                          alt="Logo" 
                          className="h-10 w-10 rounded-lg"
                        />
                        <span className="font-heading font-bold">CamSur Pickleball Club</span>
                      </div>
                    </div>
                    <nav className="flex-1 py-4">
                      {navigation.map((item) => (
                        <NavLink key={item.name} item={item} mobile />
                      ))}
                    </nav>
                    <div className="p-4 border-t border-border">
                      <Link to="/support" className="flex items-center px-4 py-3 text-muted-foreground hover:text-foreground">
                        <HelpCircle className="h-5 w-5 mr-3" />
                        Help & Support
                      </Link>
                      <Link to="/settings" className="flex items-center px-4 py-3 text-muted-foreground hover:text-foreground">
                        <Settings className="h-5 w-5 mr-3" />
                        Settings
                      </Link>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              
              <Link to="/" className="flex items-center space-x-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pbclub-scheduler/artifacts/s9rwat8e_pickleballlogo.jpg" 
                  alt="Logo" 
                  className="h-9 w-9 rounded-lg"
                />
                <span className="font-heading font-bold text-lg hidden sm:block">CamSur Pickleball Club</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigation.slice(0, 5).map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
              <NavLink item={{ name: 'Membership', href: '/memberships', icon: CreditCard }} />
            </nav>

            {/* Right Side */}
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-btn">
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <Button 
                variant="ghost" 
                size="icon" 
                className="relative"
                onClick={() => navigate('/notifications')}
                data-testid="notifications-btn"
              >
                <Bell className="h-5 w-5" />
                <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs">
                  3
                </Badge>
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2 px-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm font-medium max-w-[100px] truncate">
                      {user?.name?.split(' ')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                    <div className="flex items-center mt-2">
                      <Gift className="h-4 w-4 mr-1 text-secondary" />
                      <span className="text-sm font-medium">{user?.loyalty_points || 0} points</span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-bookings')}>
                    <Calendar className="h-4 w-4 mr-2" />
                    My Bookings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/support')}>
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Help
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>
    </div>
  );
};

export default Layout;
